import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Button, Typography, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  FileImageOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  ThunderboltOutlined,
  SwapOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  HeartOutlined,
  HeartFilled,
} from '@ant-design/icons';
import {
  isFeatureExpected,
  saveLocalFeedback,
  submitFeedbackToServer,
  getAllFeatureExpectedCounts,
} from '../../utils/feedbackUtils';
import './index.css';
import { useTranslation } from '../../hooks/useTranslation';

const { Title, Paragraph } = Typography;

interface ToolConfig {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: Array<{ text: string; color: string }>;
  buttonText: string;
  buttonIcon: React.ReactNode;
  gradient: string;
  iconBgColor: string;
  comingSoon: boolean;
}

const ImageProcessing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expectedCounts, setExpectedCounts] = useState<Record<string, number>>({});
  const [localExpected, setLocalExpected] = useState<Record<string, boolean>>({});
  const [loadingTools, setLoadingTools] = useState<Set<string>>(new Set());

  // 初始化本地期待状态和期待人数
  useEffect(() => {
    const toolKeys: string[] = [
      'watermark',
      'ai-photo',
      'white-background',
      'compress',
      'format-convert',
      'video-to-gif',
      'enhance',
    ];
    const localState: Record<string, boolean> = {};
    toolKeys.forEach((key) => {
      localState[key] = isFeatureExpected(key);
    });
    setLocalExpected(localState);

    // 从服务器加载期待人数
    loadExpectedCounts();
  }, []);

  // 加载期待人数
  const loadExpectedCounts = async () => {
    try {
      const counts = await getAllFeatureExpectedCounts();
      setExpectedCounts(counts);
    } catch (error) {
      console.error('Failed to load expected counts:', error);
      // 失败时使用空对象，不显示人数
    }
  };

  // 处理期待按钮点击
  const handleExpectClick = async (toolKey: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡

    const isCurrentlyExpected = localExpected[toolKey] || false;
    const newExpectedState = !isCurrentlyExpected;

    // 更新本地状态
    setLocalExpected((prev) => ({
      ...prev,
      [toolKey]: newExpectedState,
    }));

    // 保存到本地存储
    saveLocalFeedback(toolKey, newExpectedState);

    // 显示提示信息
    if (newExpectedState) {
      message.success(t('imageProcessing.feedbackThanks'));
    } else {
      message.info(t('imageProcessing.expectCancelled'));
    }

    // 可选：提交到服务器
    setLoadingTools((prev) => new Set(prev).add(toolKey));
    try {
      const result = await submitFeedbackToServer(toolKey, newExpectedState);
      if (result.success && result.count !== undefined) {
        setExpectedCounts((prev) => ({
          ...prev,
          [toolKey]: result.count || 0,
        }));
      }
    } catch (error) {
      // 错误已在 submitFeedbackToServer 中处理
    } finally {
      setLoadingTools((prev) => {
        const next = new Set(prev);
        next.delete(toolKey);
        return next;
      });
    }
  };

  const tools: ToolConfig[] = [
    // 已开启的功能放在前面
    {
      key: 'compress',
      icon: <ThunderboltOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: t('imageProcessing.tools.compress.title'),
      description: t('imageProcessing.tools.compress.description'),
      tags: [
        { text: t('imageProcessing.tools.compress.tag1'), color: 'orange' },
        { text: t('imageProcessing.tools.compress.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.compress.tag3'), color: 'red' },
      ],
      buttonText: t('imageProcessing.tools.compress.buttonText'),
      buttonIcon: <ThunderboltOutlined />,
      gradient: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
      iconBgColor: '#fa8c16',
      comingSoon: false,
    },
    {
      key: 'format-convert',
      icon: <SwapOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: t('imageProcessing.tools.formatConvert.title'),
      description: t('imageProcessing.tools.formatConvert.description'),
      tags: [
        { text: t('imageProcessing.tools.formatConvert.tag1'), color: 'purple' },
        { text: t('imageProcessing.tools.formatConvert.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.formatConvert.tag3'), color: 'red' },
      ],
      buttonText: t('imageProcessing.tools.formatConvert.buttonText'),
      buttonIcon: <SwapOutlined />,
      gradient: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
      iconBgColor: '#722ed1',
      comingSoon: false,
    },
    {
      key: 'video-to-gif',
      icon: <VideoCameraOutlined style={{ fontSize: '32px', color: '#08979c' }} />,
      title: t('imageProcessing.tools.videoToGif.title'),
      description: t('imageProcessing.tools.videoToGif.description'),
      tags: [
        { text: t('imageProcessing.tools.videoToGif.tag1'), color: 'cyan' },
        { text: t('imageProcessing.tools.videoToGif.tag2'), color: 'gold' },
        { text: t('imageProcessing.tools.videoToGif.tag3'), color: 'green' },
      ],
      buttonText: t('imageProcessing.tools.videoToGif.buttonText'),
      buttonIcon: <VideoCameraOutlined />,
      gradient: 'linear-gradient(135deg, #08979c 0%, #13c2c2 55%, #ffc53d 100%)',
      iconBgColor: '#08979c',
      comingSoon: false,
    },
    // 即将推出的功能放在后面
    {
      key: 'watermark',
      icon: <FileImageOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: t('imageProcessing.tools.watermark.title'),
      description: t('imageProcessing.tools.watermark.description'),
      tags: [
        { text: t('imageProcessing.tools.watermark.tag1'), color: 'blue' },
        { text: t('imageProcessing.tools.watermark.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.watermark.tag3'), color: 'cyan' },
      ],
      buttonText: t('imageProcessing.tools.watermark.buttonText'),
      buttonIcon: <FileImageOutlined />,
      gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
      iconBgColor: '#0f766e',
      comingSoon: false,
    },
    {
      key: 'ai-photo',
      icon: <IdcardOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: t('imageProcessing.tools.aiPhoto.title'),
      description: t('imageProcessing.tools.aiPhoto.description'),
      tags: [
        { text: t('imageProcessing.tools.aiPhoto.tag1'), color: 'blue' },
        { text: t('imageProcessing.tools.aiPhoto.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.aiPhoto.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.aiPhoto.buttonText'),
      buttonIcon: <PictureOutlined />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #48c6ef 100%)',
      iconBgColor: '#1890ff',
      comingSoon: true,
    },
    {
      key: 'white-background',
      icon: <ShoppingOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: t('imageProcessing.tools.whiteBackground.title'),
      description: t('imageProcessing.tools.whiteBackground.description'),
      tags: [
        { text: t('imageProcessing.tools.whiteBackground.tag1'), color: 'orange' },
        { text: t('imageProcessing.tools.whiteBackground.tag2'), color: 'green' },
        { text: t('imageProcessing.tools.whiteBackground.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.whiteBackground.buttonText'),
      buttonIcon: <ShoppingOutlined />,
      gradient: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
      iconBgColor: '#fa8c16',
      comingSoon: true,
    },
    {
      key: 'enhance',
      icon: <PictureOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: t('imageProcessing.tools.enhance.title'),
      description: t('imageProcessing.tools.enhance.description'),
      tags: [
        { text: t('imageProcessing.tools.enhance.tag1'), color: 'purple' },
        { text: t('imageProcessing.tools.enhance.tag2'), color: 'cyan' },
        { text: t('imageProcessing.tools.enhance.tag3'), color: 'default' },
      ],
      buttonText: t('imageProcessing.tools.enhance.buttonText'),
      buttonIcon: <PictureOutlined />,
      gradient: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
      iconBgColor: '#722ed1',
      comingSoon: true,
    },
  ];

  const handleToolClick = (tool: ToolConfig) => {
    if (tool.comingSoon) {
      // 可以显示提示信息
      return;
    }
    
    // 根据工具key进行路由跳转
    if (tool.key === 'format-convert') {
      navigate('/format-convert');
    }
    if (tool.key === 'compress') {
      navigate('/image-compress');
    }
    if (tool.key === 'video-to-gif') {
      navigate('/video-to-gif');
    }
    if (tool.key === 'watermark') {
      navigate('/image-watermark');
    }
  };

  return (
    <div className="image-processing-page">
      {/* Banner 区域 */}
      <div className="processing-banner">
        <div className="banner-content">
          <span className="banner-icon">🔧</span>
          <span className="banner-text">
            {t('imageProcessing.bannerText')}
          </span>
        </div>
      </div>

      {/* 工具卡片区域 */}
      <div className="processing-content">
        <Row gutter={[24, 24]}>
          {tools.map((tool) => (
            <Col xs={24} sm={12} lg={8} key={tool.key}>
              <Card
                className="tool-card"
                hoverable={!tool.comingSoon}
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className="tool-card-header">
                  <div
                    className="tool-icon-wrapper"
                    style={{ backgroundColor: `${tool.iconBgColor}15` }}
                  >
                    {tool.icon}
                  </div>
                </div>
                <div className="tool-card-body">
                  <div className="tool-title-row">
                    <Title level={4} className="tool-title">
                      {tool.title}
                    </Title>
                    {tool.comingSoon && (
                      <Button
                        type="text"
                        size="small"
                        icon={localExpected[tool.key] ? <HeartFilled style={{ fontSize: 12 }} /> : <HeartOutlined style={{ fontSize: 12 }} />}
                        loading={loadingTools.has(tool.key)}
                        onClick={(e) => handleExpectClick(tool.key, e)}
                        className={`expect-button ${localExpected[tool.key] ? 'expected' : ''}`}
                        title={localExpected[tool.key] ? t('imageProcessing.expectedFeature') : t('imageProcessing.expectFeature')}
                        style={{ fontSize: 12, height: 24, padding: '0 8px' }}
                      >
                        {localExpected[tool.key] ? t('imageProcessing.expected') : t('imageProcessing.expect')}
                        {expectedCounts[tool.key] !== undefined && expectedCounts[tool.key] > 0 && (
                          <span className="expect-count">({expectedCounts[tool.key]})</span>
                        )}
                      </Button>
                    )}
                  </div>
                  <Paragraph className="tool-description" ellipsis={{ rows: 2 }}>
                    {tool.description}
                  </Paragraph>
                  <div className="tool-tags">
                    {tool.tags.map((tag, index) => (
                      <Tag key={index} color={tag.color} style={{ marginBottom: '8px' }}>
                        {tag.text}
                      </Tag>
                    ))}
                  </div>
                </div>
                <div className="tool-card-footer">
                  <Button
                    type="primary"
                    size="middle"
                    block
                    icon={tool.buttonIcon}
                    disabled={tool.comingSoon}
                    onClick={() => handleToolClick(tool)}
                    style={{
                      background: tool.comingSoon ? '#d9d9d9' : tool.gradient,
                      border: 'none',
                      height: '36px',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {tool.buttonText}
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default ImageProcessing;
